import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";
import { Icon } from "../../";

const STREAM_ORDERS_LOGO_URL =
  "https://d3vj0kew82apqn.cloudfront.net/stream-orders-logo.jpg";

const MODAL_ID = "hubspot-modal-stream-orders";
const FORM_CONTAINER_ID = "hubspot-form-container-stream-orders";
const SKELETON_ID = "hubspot-loading-skeleton-stream-orders";

const StreamOrdersIntegrationCard = () => {
  const { t } = useTranslation(["integrations", "common"]);
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const posConfigureEnabled = isFeatureEnabled("pos_configure_enabled");

  const [isLoadingScript, setIsLoadingScript] = useState(!window.hbspt);
  const [logoError, setLogoError] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!window.hbspt) {
      const script = document.createElement("script");
      script.src = "//js.hsforms.net/forms/embed/v2.js";
      script.charset = "utf-8";
      script.type = "text/javascript";
      script.async = true;

      script.onload = () => {
        if (isMountedRef.current) {
          setIsLoadingScript(false);
        }
      };

      script.onerror = () => {
        if (isMountedRef.current) {
          setIsLoadingScript(false);
        }
        console.error("Failed to load HubSpot forms script");
      };

      document.body.appendChild(script);
    } else {
      setIsLoadingScript(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleContactUs = (e) => {
    e.stopPropagation();

    if (document.getElementById(MODAL_ID)) {
      return;
    }

    let formLoadTimeout = null;
    let retryTimeout = null;
    let retryCount = 0;
    const MAX_RETRIES = 50;

    const modalContainer = document.createElement("div");
    modalContainer.id = MODAL_ID;
    modalContainer.style.cssText = `
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 1rem;
    `;

    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background-color: #1f2937;
      border-radius: 0.5rem;
      padding: 2rem;
      max-width: 600px;
      width: 100%;
      min-height: 500px;
      max-height: 90vh;
      overflow-y: auto;
      position: relative;
    `;

    const closeButton = document.createElement("button");
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    closeButton.style.cssText = `
      position: absolute;
      top: 1rem;
      right: 1rem;
      background: transparent;
      border: none;
      color: #9ca3af;
      cursor: pointer;
      padding: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.25rem;
      transition: all 0.2s;
    `;

    const handleMouseOver = () => {
      closeButton.style.color = "#ffffff";
      closeButton.style.backgroundColor = "#374151";
    };
    const handleMouseOut = () => {
      closeButton.style.color = "#9ca3af";
      closeButton.style.backgroundColor = "transparent";
    };

    closeButton.addEventListener("mouseover", handleMouseOver);
    closeButton.addEventListener("mouseout", handleMouseOut);

    const cleanup = () => {
      if (formLoadTimeout) clearTimeout(formLoadTimeout);
      if (retryTimeout) clearTimeout(retryTimeout);

      closeButton.removeEventListener("mouseover", handleMouseOver);
      closeButton.removeEventListener("mouseout", handleMouseOut);
      closeButton.removeEventListener("click", cleanup);
      modalContainer.removeEventListener("click", handleBackdropClick);

      if (document.body.contains(modalContainer)) {
        document.body.removeChild(modalContainer);
      }
    };

    closeButton.addEventListener("click", cleanup);

    const formContainer = document.createElement("div");
    formContainer.id = FORM_CONTAINER_ID;
    formContainer.style.cssText = `
      margin-top: 1rem;
    `;

    const skeleton = document.createElement("div");
    skeleton.id = SKELETON_ID;
    skeleton.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    `;
    skeleton.innerHTML = `
      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      </style>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
        <div>
          <div style="height: 1rem; width: 5rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
        <div>
          <div style="height: 1rem; width: 5rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
      </div>
      <div>
        <div style="height: 1rem; width: 3rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
      </div>
      <div>
        <div style="height: 1rem; width: 7rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="display: grid; grid-template-columns: 150px 1fr; gap: 0.75rem;">
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
          <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
        </div>
      </div>
      <div>
        <div style="height: 1rem; width: 11rem; background-color: #4b5563; border-radius: 0.25rem; margin-bottom: 0.5rem;"></div>
        <div style="height: 2.75rem; background-color: #374151; border-radius: 0.375rem;"></div>
      </div>
      <div style="height: 2.75rem; background-color: #6b7280; border-radius: 0.375rem; width: 50%; margin-top: 0.5rem;"></div>
    `;
    formContainer.appendChild(skeleton);

    const title = document.createElement("h2");
    title.textContent = t("pos.streamOrders.contactModalTitle");
    title.style.cssText = `
      color: #ffffff;
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 1rem;
      padding-right: 2rem;
    `;

    modalContent.appendChild(closeButton);
    modalContent.appendChild(title);
    modalContent.appendChild(formContainer);
    modalContainer.appendChild(modalContent);
    document.body.appendChild(modalContainer);

    const handleBackdropClick = (ev) => {
      if (ev.target === modalContainer) {
        cleanup();
      }
    };
    modalContainer.addEventListener("click", handleBackdropClick);

    const loadForm = () => {
      if (window.hbspt) {
        formLoadTimeout = setTimeout(() => {
          if (!document.body.contains(modalContainer)) return;

          const skeletonElement = document.getElementById(SKELETON_ID);
          if (skeletonElement) {
            skeletonElement.remove();
          }

          window.hbspt.forms.create({
            portalId: "46622027",
            formId: "75de65b2-e874-4be8-8f33-d126fb5cf387",
            region: "na1",
            target: `#${FORM_CONTAINER_ID}`,
            onFormSubmitted: () => {
              toast.success(t("pos.toasts.formSubmitted"));
              cleanup();
            },
          });
        }, 800);
      } else {
        retryCount++;
        if (retryCount < MAX_RETRIES) {
          retryTimeout = setTimeout(loadForm, 100);
        } else {
          console.error("HubSpot script failed to load after maximum retries");
          toast.error(t("pos.toasts.contactFormLoadError"));
          cleanup();
        }
      }
    };

    loadForm();
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:bg-gray-800/50">
      <div className="mb-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-white">
              {!logoError ? (
                <img
                  src={STREAM_ORDERS_LOGO_URL}
                  alt={t("pos.streamOrders.logoAlt")}
                  className="h-full w-full object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-lg font-bold text-gray-900">
                  {t("pos.streamOrders.initials")}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-lg font-semibold text-white">
                {t("pos.streamOrders.title")}
              </h3>
              <p className="text-sm text-gray-400">
                {t("pos.streamOrders.subtitle")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-auto space-y-2 border-t border-gray-800 pt-4">
        {posConfigureEnabled ? (
          <button
            type="button"
            onClick={handleContactUs}
            disabled={isLoadingScript}
            className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t("contactUs")}
          >
            {isLoadingScript ? (
              <>
                <svg
                  className="mr-2 -ml-1 h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{t("common:states.loading")}</span>
              </>
            ) : (
              <span>{t("contactUs")}</span>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              showMessenger();
            }}
            className="flex w-full items-center justify-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600"
            aria-label={t("contactUs")}
          >
            <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
            <span>{t("contactUs")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StreamOrdersIntegrationCard;
